import { Formik, Field, ErrorMessage } from 'formik';
import React, { useMemo, useState } from 'react';
import * as Yup from 'yup';
import { Button } from 'antd';
import { IOptionValidationRule, IQuestionOption, QuestionType } from '../../../types';

const RAL_PRESETS = [
  { code: '6005', name: 'Moss Green' },
  { code: '7001', name: 'Elephant Grey' },
  { code: '7015', name: 'Slate Grey' },
  { code: '7032', name: 'Pebble Grey' },
  { code: '7044', name: 'Silk Grey' },
  { code: '9005', name: 'Jet Black' },
  { code: '9010', name: 'Pure White' },
  { code: '9016', name: 'Traffic White' },
  { code: '9001', name: 'Cream' },
  { code: '7016', name: 'Anthracite Grey' },
  { code: '7038', name: 'Agate Grey' },
  { code: '8022', name: 'Black/Brown' },
];

export interface FieldConstraint {
  allow160?: boolean;
}

type SplitMode = 'custom' | 'equal' | 'symmetric';

/**
 * Metadata key used to persist the user's chosen width-split tag for couple
 * dimension forms inside response.form. Stored alongside the regular field
 * values so that re-rendering the form (e.g. in the edit drawer) can restore
 * the original tag selection instead of resetting to "Custom".
 */
const SPLIT_MODE_FORM_KEY = '__split_mode__';

const isSplitMode = (v: unknown): v is SplitMode =>
  v === 'custom' || v === 'equal' || v === 'symmetric';

/** Couple windows can span multiple frames so we allow a wider overall width. */
const COUPLE_OVERALL_WIDTH_MAX = 4000;

interface FormCardProps {
  options: IQuestionOption[];
  onSubmit: (values: any) => void;
  savedValues?: any;
  submitButtonText?: string;
  validationRules?: IOptionValidationRule[];
  heightConstraint?: number;
  percentageSumMax?: number;
  fieldConstraints?: Record<string, FieldConstraint>;
  /** When true, renders the couple-window width-split UI */
  isCoupleDimension?: boolean;
  /** When true, shoulder height is auto-calculated (overall_height - width/2) and disabled */
  isTrueArch?: boolean;
}

export const FormCard: React.FC<FormCardProps> = ({
  options,
  onSubmit,
  savedValues,
  submitButtonText = 'Continue',
  validationRules = [],
  heightConstraint,
  percentageSumMax,
  fieldConstraints,
  isCoupleDimension = false,
  isTrueArch = false,
}) => {
  // ─── Couple/Bay width-split state (hooks must always be called) ─────────
  // Restore the user's previously-chosen tag if persisted in savedValues — this
  // keeps the edit-drawer view consistent with the original submission instead
  // of always defaulting to "Custom" on re-open.
  const [splitMode, setSplitMode] = useState<SplitMode>(() => {
    const saved = savedValues?.[SPLIT_MODE_FORM_KEY];
    return isSplitMode(saved) ? saved : 'custom';
  });

  // For Bay windows that have no overall_width_(mm) field, track a synthetic
  // overall width in local state so equal/symmetric modes can distribute widths.
  const [bayOverallWidth, setBayOverallWidth] = useState<string>(() => {
    if (savedValues) {
      const saved = savedValues?.['overall_width_(mm)'];
      if (saved) return String(saved);
    }
    return '';
  });

  // Tracks which RAL code fields are in free-text "Other" mode
  const [ralOtherMode, setRalOtherMode] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    options.forEach(opt => {
      if (opt.responseValue?.includes('ral_code')) {
        const saved = savedValues?.[opt.responseValue ?? ''];
        init[opt.responseValue] = !!saved && !RAL_PRESETS.some(p => p.code === saved);
      }
    });
    return init;
  });

  const heightOpt = useMemo(
    () => options.find(o => o.responseValue === 'overall_height_(mm)'),
    [options],
  );
  const overallWidthOpt = useMemo(
    () => options.find(o =>
      o.responseValue === 'overall_width_(mm)' || o.responseValue === 'width_(mm)'
    ),
    [options],
  );
  const frameWidthOpts = useMemo(
    () =>
      options
        .filter(o => /^window_\d+_width_\(mm\)$/.test(o.responseValue ?? ''))
        .sort((a, b) => {
          const n = (s: string) => parseInt((s.match(/\d+/) ?? ['0'])[0]);
          return n(a.responseValue ?? '') - n(b.responseValue ?? '');
        }),
    [options],
  );
  const shoulderHeightOpt = useMemo(
    () => options.find(o => o.responseValue === 'shoulder_height_(mm)'),
    [options],
  );
  const bayPoleOpts = useMemo(
    () => options.filter(o => /^bay_poles/i.test(o.responseValue ?? '')),
    [options],
  );
  const otherOpts = useMemo(
    () => options.filter(
      o =>
        o !== heightOpt &&
        o !== overallWidthOpt &&
        !frameWidthOpts.includes(o) &&
        !bayPoleOpts.includes(o),
    ),
    [options, heightOpt, overallWidthOpt, frameWidthOpts, bayPoleOpts],
  );

  const frameCount = frameWidthOpts.length;
  const overallWidthKey = overallWidthOpt?.responseValue ?? 'overall_width_(mm)';
  // Bay windows don't have an overall_width_(mm) field — splitter still applies.
  const showCoupleSplitter = isCoupleDimension && frameCount >= 2 && !!heightOpt;

  const availableModes = useMemo((): SplitMode[] => {
    if (!showCoupleSplitter) return [];
    const modes: SplitMode[] = ['custom', 'equal'];
    if (frameCount === 3) modes.push('symmetric');
    return modes;
  }, [showCoupleSplitter, frameCount]);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  const getValidationRuleForField = (fieldOption: IQuestionOption): IOptionValidationRule | null =>
    validationRules.find(rule => rule.dgma_options.includes(fieldOption.optionId)) ?? null;

  const modeLabel = (mode: SplitMode): string => {
    switch (mode) {
      case 'custom': return 'Custom';
      case 'equal': return 'Equal Split';
      case 'symmetric': return '¼ + ½ + ¼ (3-part)';
    }
  };

  const to2dp = (n: number) => Math.round(n * 100) / 100;

  const applyWidthMode = (
    mode: SplitMode,
    overallWidth: number,
    setFV: (f: string, v: any, shouldValidate?: boolean) => void,
  ) => {
    const ow = to2dp(overallWidth);
    if (mode === 'equal') {
      const perFrame = to2dp(ow / frameCount);
      frameWidthOpts.forEach(o => setFV(o.responseValue ?? '', perFrame, true));
    } else if (mode === 'symmetric' && frameCount === 3) {
      const q = to2dp(ow * 0.25);
      setFV(frameWidthOpts[0]!.responseValue ?? '', q, true);
      setFV(frameWidthOpts[1]!.responseValue ?? '', to2dp(ow - 2 * q), true);
      setFV(frameWidthOpts[2]!.responseValue ?? '', q, true);
    }
  };

  // ─── Validation ───────────────────────────────────────────────────────────

  const numericKeys = options
    .filter(o => o.inputType === QuestionType.NUMBER_INPUT)
    .map(o => o.responseValue || o.optionId);

  const validateSums = (values: any): Record<string, string> => {
    const errs: Record<string, string> = {};
    const sum = numericKeys.reduce((acc, k) => acc + (Number(values[k]) || 0), 0);
    if (heightConstraint) {
      const anyExceedsMax = numericKeys.some(k => (Number(values[k]) || 0) > heightConstraint);
      if (!anyExceedsMax && sum > heightConstraint) {
        errs['__height_sum__'] = `Top Sash + Bottom Sash must not exceed window height (${heightConstraint}mm)`;
      }
    }
    if (percentageSumMax != null) {
      const anyExceedsMax = numericKeys.some(k => (Number(values[k]) || 0) > percentageSumMax);
      if (!anyExceedsMax && sum > percentageSumMax) {
        errs['__percentage_sum__'] = `Top + Bottom must not exceed ${percentageSumMax}%`;
      }
    }
    return errs;
  };

  const validationSchema = useMemo(() => {
    const schema: any = {};
    options.forEach(option => {
      const fieldName = option.responseValue || option.optionId;
      const isNumericInput = option.inputType === QuestionType.NUMBER_INPUT;
      const isFrameWidth = /^window_\d+_width_\(mm\)$/.test(option.responseValue ?? '');
      const isOverallWidth = showCoupleSplitter && option === overallWidthOpt;
      const isBayJack = /^bay_jack/i.test(option.responseValue ?? '');
      const isOptionalField = /\(optional\)/i.test(option.optionText ?? '');
      // In preset modes frame widths are hidden/computed; in custom mode overall width is computed
      const autoComputed = showCoupleSplitter && (
        (isFrameWidth && splitMode !== 'custom') ||
        (isOverallWidth && splitMode === 'custom')
      );

      // Auto-computed, bay-pole, explicitly-optional, and true-arch shoulder height bypass required validation.
      if (autoComputed || isBayJack || isOptionalField || /^bay_poles/i.test(option.responseValue ?? '') ||
          (isTrueArch && option.responseValue === 'shoulder_height_(mm)')) {
        schema[fieldName] = Yup.mixed().notRequired();
        return;
      }

      let validator: any = isNumericInput
        ? Yup.number().typeError(`${option.optionText} must be a number`)
        : Yup.string();

      if (isNumericInput) {
        validator = validator.required(`${option.optionText} is required`);
      }

      const rule = getValidationRuleForField(option);
      if (rule && isNumericInput) {
        if (rule.dogma_min !== undefined)
          validator = validator.min(rule.dogma_min, `Minimum ${rule.dogma_min}mm required`);
        // For the couple overall-width field, use the extended couple max instead
        // of the per-frame max stored in the validation rule.
        const effectiveMax = isOverallWidth ? COUPLE_OVERALL_WIDTH_MAX : rule.dogma_max;
        if (effectiveMax != null && effectiveMax !== 0)
          validator = validator.max(effectiveMax, `Maximum ${effectiveMax}mm allowed`);
      }

      if (!isNumericInput && option.responseValue?.includes('ral_code')) {
        validator = validator.test(
          'ral-classic',
          'RAL code must be 4 digits with 0 as the 2nd digit (e.g. 9001, 7016)',
          (v: string | undefined) => !v || /^\d0\d{2}$/.test(v),
        );
      }

      const isAngleField = isNumericInput && (
        /\(°\)$/.test(option.responseValue ?? '') ||
        /^horn_angle/i.test(option.responseValue ?? '')
      );
      if (isAngleField) {
        validator = validator.test(
          'angle-value',
          'Angle must be 90°, 113°–157°, 160°, or 180°',
          (v: number | undefined) =>
            v == null || v === 90 || (v >= 113 && v <= 157) || v === 160 || v === 180,
        );
      }

      if (isNumericInput && option.responseValue === 'shoulder_height_(mm)') {
        validator = validator.test(
          'shoulder-max-overall-height',
          'Shoulder height cannot exceed the overall height',
          function (this: any, v: number | undefined) {
            if (v == null) return true;
            const overall = Number((this.parent as any)['overall_height_(mm)']);
            if (!overall) return true;
            return v <= overall;
          },
        );
      }

      if (percentageSumMax != null && isNumericInput)
        validator = validator.min(0, 'Value must be at least 0%').max(percentageSumMax, `Value cannot exceed ${percentageSumMax}%`);

      if (heightConstraint && isNumericInput)
        validator = validator.max(heightConstraint, `Cannot exceed window height (${heightConstraint}mm)`);

      schema[fieldName] = validator;
    });

    let objectSchema = Yup.object().shape(schema);

    // Spray colour pairs: at least one of each pair must be filled
    const sprayPairs = [
      { keys: ['internal_farrow_&_ball', 'internal_ral_code'], label: 'Internal Farrow & Ball or Internal RAL Code' },
      { keys: ['external_farrow_&_ball', 'external_ral_code'], label: 'External Farrow & Ball or External RAL Code' },
    ];
    sprayPairs.forEach(({ keys, label }) => {
      const presentKeys = keys.filter(k => options.some(o => o.responseValue === k));
      if (presentKeys.length === 2) {
        objectSchema = objectSchema.test(
          `spray-required-${presentKeys[0]}`,
          `Please enter either ${label}`,
          function (this: any, values: any) {
            if (values?.[presentKeys[0]!] || values?.[presentKeys[1]!]) return true;
            return this.createError({ path: presentKeys[0]!, message: `Please enter either ${label}` });
          },
        );
      }
    });

    // Couple custom mode: sum of all per-frame widths must not exceed 4000 mm
    if (showCoupleSplitter && splitMode === 'custom' && frameWidthOpts.length >= 2) {
      const lastFrameKey = frameWidthOpts[frameWidthOpts.length - 1]!.responseValue ?? '';
      objectSchema = objectSchema.test(
        'couple-frame-width-sum',
        `Total width of all windows must not exceed ${COUPLE_OVERALL_WIDTH_MAX}mm`,
        function (values: any) {
          const sum = frameWidthOpts.reduce(
            (acc, o) => acc + (Number(values?.[o.responseValue ?? '']) || 0), 0,
          );
          if (sum <= COUPLE_OVERALL_WIDTH_MAX) return true;
          return this.createError({
            path: lastFrameKey,
            message: `Total width (${sum}mm) must not exceed ${COUPLE_OVERALL_WIDTH_MAX}mm`,
          });
        },
      );
    }

    // Bay angles: prevent all angles being 180° (that would be a flat window, not a bay)
    const angleKeys = options
      .filter(o => /\(°\)$/.test(o.responseValue ?? '') && o.inputType === QuestionType.NUMBER_INPUT)
      .map(o => o.responseValue ?? '');
    if (angleKeys.length >= 2) {
      const lastAngleKey = angleKeys[angleKeys.length - 1];
      objectSchema = objectSchema.test(
        'bay-angles-not-all-180',
        'All angles cannot be 180°',
        function (values: any) {
          const allFilled = angleKeys.every(k => values?.[k] != null && values[k] !== '');
          if (!allFilled) return true;
          const allAre180 = angleKeys.every(k => Number(values[k]) === 180);
          if (!allAre180) return true;
          return this.createError({
            path: lastAngleKey,
            message: 'All angles cannot be 180° — this would make a flat window, not a bay',
          });
        },
      );
    }

    return objectSchema;
  }, [options, validationRules, heightConstraint, percentageSumMax, fieldConstraints, showCoupleSplitter, splitMode, frameWidthOpts]);

  const initialValues = useMemo(() => {
    const vals: any = {};
    options.forEach(option => {
      const fieldName = option.responseValue || option.optionId;
      const isBayPoles = /^bay_poles/i.test(option.responseValue ?? '');
      // Bay poles are always 2 and hidden; ignore any previously-saved value.
      vals[fieldName] = isBayPoles ? 2 : (savedValues?.[fieldName] ?? '');
    });
    // For True Arch, always derive shoulder height from width and overall height.
    if (isTrueArch && shoulderHeightOpt) {
      const w = Number(savedValues?.['overall_width_(mm)'] || savedValues?.['width_(mm)'] || 0);
      const h = Number(savedValues?.['overall_height_(mm)'] || 0);
      vals['shoulder_height_(mm)'] = w > 0 && h > 0 ? Math.max(0, h - w / 2) : '';
    }
    return vals;
  }, [options, savedValues, frameCount, isTrueArch, shoulderHeightOpt]);

  const handleInternalSubmit = (values: any) => {
    if (showCoupleSplitter) {
      if (splitMode === 'custom') {
        // Compute overall width as sum of frame widths
        const frameSum = frameWidthOpts.reduce(
          (acc, o) => acc + (Number(values[o.responseValue ?? '']) || 0), 0,
        );
        onSubmit({ ...values, [overallWidthKey]: frameSum, [SPLIT_MODE_FORM_KEY]: splitMode });
        return;
      }
      // Preset modes: distribute overall width into frame widths.
      // For Bay windows (no overallWidthOpt), read from local bayOverallWidth state.
      const ow = overallWidthOpt
        ? Number(values[overallWidthKey])
        : Number(bayOverallWidth);
      if (ow > 0) {
        const computed: any = { [overallWidthKey]: ow };
        if (splitMode === 'equal') {
          const perFrame = to2dp(ow / frameCount);
          frameWidthOpts.forEach(o => (computed[o.responseValue ?? ''] = perFrame));
        } else if (splitMode === 'symmetric' && frameCount === 3) {
          const q = to2dp(ow * 0.25);
          computed[frameWidthOpts[0]!.responseValue ?? ''] = q;
          computed[frameWidthOpts[1]!.responseValue ?? ''] = to2dp(ow - 2 * q);
          computed[frameWidthOpts[2]!.responseValue ?? ''] = q;
        }
        onSubmit({ ...values, ...computed, [SPLIT_MODE_FORM_KEY]: splitMode });
        return;
      }
    }
    onSubmit(values);
  };

  if (!options || options.length === 0) {
    return null;
  }

  // ─── Standard field renderer (used in default grid + otherOpts) ──────────
  const renderStandardField = (option: IQuestionOption, setFieldValue: any) => {
    const fieldName = option.responseValue || option.optionId;
    const isNumericInput = option.inputType === QuestionType.NUMBER_INPUT;
    const isRalCode = !isNumericInput && !!option.responseValue?.includes('ral_code');
    const rule = getValidationRuleForField(option);
    const needsAutoPopulate = isNumericInput && (!!heightConstraint || percentageSumMax != null);
    const siblingOption = needsAutoPopulate
      ? options.find(o => {
        const k = o.responseValue || o.optionId;
        return k !== fieldName && o.inputType === QuestionType.NUMBER_INPUT;
      })
      : undefined;
    const siblingKey = siblingOption ? (siblingOption.responseValue || siblingOption.optionId) : '';
    const sumMax = heightConstraint ?? percentageSumMax;

    return (
      <div key={option.optionId} className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <label htmlFor={fieldName} className="block text-sm font-medium text-neutral-700">
            {option.optionText}
          </label>
          {isRalCode && ralOtherMode[fieldName] && (
            <button
              type="button"
              onClick={() => {
                setRalOtherMode(prev => ({ ...prev, [fieldName]: false }));
                setFieldValue(fieldName, '');
              }}
              className="text-xs text-primary-600 underline"
            >
              Reset
            </button>
          )}
        </div>

        {isRalCode ? (
          ralOtherMode[fieldName] ? (
            // Free-text input when "Other" is selected
            <>
              <Field
                id={fieldName}
                name={fieldName}
                type="text"
                placeholder="e.g. 9001"
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </>
          ) : (
            // Preset dropdown
            <Field name={fieldName}>
              {({ field }: any) => (
                <select
                  id={fieldName}
                  value={field.value ?? ''}
                  onBlur={field.onBlur}
                  onChange={e => {
                    if (e.target.value === '__other__') {
                      setRalOtherMode(prev => ({ ...prev, [fieldName]: true }));
                      setFieldValue(fieldName, '');
                    } else {
                      setFieldValue(fieldName, e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                >
                  <option value="">Select RAL code</option>
                  {RAL_PRESETS.map(p => (
                    <option key={p.code} value={p.code}>{p.code} – {p.name}</option>
                  ))}
                  <option value="__other__">Other</option>
                </select>
              )}
            </Field>
          )
        ) : needsAutoPopulate ? (
          <Field name={fieldName}>
            {({ field }: any) => (
              <input
                {...field}
                id={fieldName}
                type="number"
                placeholder={option.description || option.optionText}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                onBlur={(e) => {
                  field.onBlur(e);
                  if (siblingKey && sumMax != null) {
                    const val = Number(e.target.value);
                    if (!isNaN(val) && val >= 0)
                      setFieldValue(siblingKey, Math.max(0, sumMax - val), true);
                  }
                }}
              />
            )}
          </Field>
        ) : (
          <Field
            id={fieldName}
            name={fieldName}
            type={isNumericInput ? 'number' : 'text'}
            placeholder={option.description || option.optionText}
            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        )}

        <ErrorMessage name={fieldName} component="div" className="text-danger-500 text-xs mt-1" />
        {isNumericInput && percentageSumMax != null && (
          <p className="text-[10px] text-neutral-600 mt-1">0–{percentageSumMax}%</p>
        )}
        {isNumericInput && !percentageSumMax && rule && (
          <p className="text-[10px] text-neutral-600 mt-1">
            Min: {rule.dogma_min},{' '}
            Max: {heightConstraint ? heightConstraint : rule.dogma_max}mm
            {heightConstraint && <span className="ml-1 text-amber-600">(window height)</span>}
          </p>
        )}
      </div>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validate={validateSums}
      onSubmit={handleInternalSubmit}
      validateOnChange={false}
      validateOnBlur={true}
      enableReinitialize
    >
      {({ handleSubmit, setFieldValue, errors, values }) => (
        <div className="bg-white w-fit min-w-[60%] rounded-xl shadow-sm border border-neutral-300 p-4 mt-2">

          {showCoupleSplitter ? (
            // ─── Couple dimension form ─────────────────────────────────────
            <>
              {/* Row 1 — Split mode tags */}
              <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-neutral-100">
                <span className="text-xs font-semibold text-neutral-500 mr-1">Width split:</span>
                {availableModes.map(mode => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      setSplitMode(mode);
                      if (overallWidthOpt) {
                        setFieldValue(overallWidthKey, '', false);
                      } else {
                        setBayOverallWidth('');
                      }
                      frameWidthOpts.forEach(o => setFieldValue(o.responseValue ?? '', '', false));
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${splitMode === mode
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-white text-neutral-600 border-neutral-300 hover:border-primary-400 hover:text-primary-600'
                      }`}
                  >
                    {modeLabel(mode)}
                  </button>
                ))}
              </div>

              {/* Row 2 — Overall Width (preset modes) */}
              {overallWidthOpt && splitMode !== 'custom' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    {overallWidthOpt.optionText}
                  </label>
                  <Field name={overallWidthKey}>
                    {({ field }: any) => (
                      <input
                        {...field}
                        type="number"
                        placeholder={overallWidthOpt.description || overallWidthOpt.optionText}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        onBlur={(e) => {
                          field.onBlur(e);
                          const val = Number(e.target.value);
                          if (val > 0) applyWidthMode(splitMode, val, setFieldValue);
                        }}
                      />
                    )}
                  </Field>
                  <ErrorMessage name={overallWidthKey} component="div" className="text-danger-500 text-xs mt-1" />
                  {(() => {
                    const rule = getValidationRuleForField(overallWidthOpt);
                    if (!rule) return null;
                    return (
                      <p className="text-[10px] text-neutral-600 mt-1">
                        Min: {rule.dogma_min}, Max: {COUPLE_OVERALL_WIDTH_MAX}mm
                      </p>
                    );
                  })()}
                </div>
              )}

              {/* Row 2 (Bay) — Synthetic overall width for Bay windows in preset modes */}
              {!overallWidthOpt && splitMode !== 'custom' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    Overall Width (mm)
                  </label>
                  <input
                    type="number"
                    value={bayOverallWidth}
                    onChange={e => setBayOverallWidth(e.target.value)}
                    onBlur={e => {
                      const val = Number(e.target.value);
                      if (val > 0) applyWidthMode(splitMode, val, setFieldValue);
                    }}
                    placeholder="Overall Width (mm)"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-[10px] text-neutral-600 mt-1">Max: {COUPLE_OVERALL_WIDTH_MAX}mm</p>
                </div>
              )}

              {/* Row 2 (custom) — Individual frame widths */}
              {splitMode === 'custom' && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {frameWidthOpts.map(opt => {
                    const key = opt.responseValue ?? '';
                    const rule = getValidationRuleForField(opt);
                    return (
                      <div key={opt.optionId}>
                        <label className="block text-sm font-medium mb-1 text-neutral-700">
                          {opt.optionText}
                        </label>
                        <Field name={key}>
                          {({ field }: any) => (
                            <input
                              {...field}
                              type="number"
                              placeholder={opt.description || opt.optionText}
                              className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          )}
                        </Field>
                        <ErrorMessage name={key} component="div" className="text-danger-500 text-xs mt-1" />
                        {rule && (
                          <p className="text-[10px] text-neutral-600 mt-1">
                            Min: {rule.dogma_min}, Max: {rule.dogma_max}mm
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Hidden frame width fields in preset modes */}
              {splitMode !== 'custom' && frameWidthOpts.map(opt => (
                <Field key={opt.optionId} name={opt.responseValue ?? ''} type="hidden" />
              ))}

              {/* Row 3 — Overall Height */}
              {heightOpt && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-neutral-700 mb-1">
                    {heightOpt.optionText}
                  </label>
                  <Field
                    name={heightOpt.responseValue}
                    type="number"
                    placeholder={heightOpt.description || heightOpt.optionText}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <ErrorMessage name={heightOpt.responseValue!} component="div" className="text-danger-500 text-xs mt-1" />
                  {(() => {
                    const rule = getValidationRuleForField(heightOpt);
                    return rule ? (
                      <p className="text-[10px] text-neutral-600 mt-1">
                        Min: {rule.dogma_min}, Max: {rule.dogma_max}mm
                      </p>
                    ) : null;
                  })()}
                </div>
              )}

              {/* Row 4 — Other fields (angles, bay jacks, etc.) */}
              {otherOpts.length > 0 && (
                <div className={`grid gap-3 mb-4 ${otherOpts.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {otherOpts.map(o => renderStandardField(o, setFieldValue))}
                </div>
              )}

              {/* Bay-pole hidden fields */}
              {bayPoleOpts.map(o => (
                <Field key={o.optionId} name={o.responseValue ?? o.optionId} type="hidden" />
              ))}
            </>
          ) : (
            // ─── Standard grid layout ──────────────────────────────────────
            <>
              <div className={`grid gap-3 ${options.filter(o => !/^bay_poles/i.test(o.responseValue ?? '')).length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                {options
                  .filter(o => !/^bay_poles/i.test(o.responseValue ?? ''))
                  .slice()
                  .sort((a, b) => {
                    const order = (o: IQuestionOption) => {
                      const rv = o.responseValue ?? '';
                      if (rv === 'overall_width_(mm)' || rv === 'width_(mm)') return 0;
                      if (rv === 'overall_height_(mm)') return 1;
                      return 2;
                    };
                    return order(a) - order(b);
                  })
                  .map(option => {
                    const rv = option.responseValue ?? '';
                    const isWidthField = isTrueArch && (rv === 'overall_width_(mm)' || rv === 'width_(mm)');
                    const isHeightField = isTrueArch && rv === 'overall_height_(mm)';
                    const isShoulderField = isTrueArch && rv === 'shoulder_height_(mm)';

                    if (isShoulderField) {
                      const rule = getValidationRuleForField(option);
                      return (
                        <div key={option.optionId} className="flex-1 min-w-0">
                          <label className="block text-sm font-medium text-neutral-700 mb-1">
                            {option.optionText}
                          </label>
                          <Field name="shoulder_height_(mm)">
                            {({ field }: any) => (
                              <input
                                {...field}
                                type="number"
                                disabled
                                className="w-full px-3 py-2 border border-neutral-200 rounded-lg bg-neutral-100 text-neutral-500 cursor-not-allowed"
                              />
                            )}
                          </Field>
                          {rule && (
                            <p className="text-[10px] text-neutral-600 mt-0.5">
                              Min: {rule.dogma_min}, Max: {rule.dogma_max}mm
                            </p>
                          )}
                        </div>
                      );
                    }

                    if (isWidthField || isHeightField) {
                      const fieldName = rv;
                      const rule = getValidationRuleForField(option);
                      return (
                        <div key={option.optionId} className="flex-1 min-w-0">
                          <label htmlFor={fieldName} className="block text-sm font-medium text-neutral-700 mb-1">
                            {option.optionText}
                          </label>
                          <Field name={fieldName}>
                            {({ field }: any) => (
                              <input
                                {...field}
                                id={fieldName}
                                type="number"
                                placeholder={option.description || option.optionText}
                                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                onChange={(e) => {
                                  field.onChange(e);
                                  const newVal = Number(e.target.value);
                                  const w = isWidthField ? newVal : Number(values['overall_width_(mm)'] || values['width_(mm)'] || 0);
                                  const h = isHeightField ? newVal : Number(values['overall_height_(mm)'] || 0);
                                  if (w > 0 && h > 0) {
                                    setFieldValue('shoulder_height_(mm)', Math.max(0, h - w / 2));
                                  } else {
                                    setFieldValue('shoulder_height_(mm)', '');
                                  }
                                }}
                              />
                            )}
                          </Field>
                          <ErrorMessage name={fieldName} component="div" className="text-danger-500 text-xs mt-1" />
                          {rule && (
                            <p className="text-[10px] text-neutral-600 mt-1">
                              Min: {rule.dogma_min}, Max: {rule.dogma_max}mm
                            </p>
                          )}
                        </div>
                      );
                    }

                    return renderStandardField(option, setFieldValue);
                  })}
              </div>
              {/* Bay-pole hidden fields — always submitted with a fixed value of 2 */}
              {bayPoleOpts.map(o => (
                <Field key={o.optionId} name={o.responseValue ?? o.optionId} type="hidden" />
              ))}
            </>
          )}

          {(errors as any)['__height_sum__'] && (
            <p className="text-danger-500 text-xs mt-3">{(errors as any)['__height_sum__']}</p>
          )}
          {(errors as any)['__percentage_sum__'] && (
            <p className="text-danger-500 text-xs mt-3">{(errors as any)['__percentage_sum__']}</p>
          )}

          <Button
            type="primary"
            size="large"
            block
            onClick={() => handleSubmit()}
            className="font-semibold text-[16px] h-[52px] mt-4"
          >
            {submitButtonText}
          </Button>
        </div>
      )}
    </Formik>
  );
};
